import { Actor, type HttpAgent, type Identity } from "@icp-sdk/core/agent";
import {
	amountToNat,
	createHttpAgent,
	fetchIcrcBalanceE8,
	fetchUsdmTransferFee,
	formatCandidError,
	natToAmount,
	resolveTransferLedgerId,
	transferUsdmToLiquidatorEscrow,
	type ProtocolActor,
} from "@motokomaker/ic-client";
import { idlFactory } from "@/declarations/agent_gateway.did.js";
import type { _SERVICE } from "@/declarations/agent_gateway.did.d.ts";
import { getIcConfig } from "@/lib/network";
import type { HuntTargetUi } from "@/lib/hunt-match";

const AGENT_GATEWAY_CANISTER_ID =
	import.meta.env.VITE_CANISTER_ID_AGENT_GATEWAY ??
	import.meta.env.VITE_AGENT_GATEWAY_CANISTER_ID ??
	"";

export const AUTOPILOT_KEY = "motokomaker.v2.agentAutopilot";
export const AUTOPILOT_DAILY_CAP_KEY = "motokomaker.v2.agentAutopilotDailyCapUsdm";
export const AUTOPILOT_SPEND_KEY = "motokomaker.v2.agentAutopilotSpend";
/** Client-side Autopilot daily USDM spend ceiling per browser (UTC day). */
export const DEFAULT_AUTOPILOT_DAILY_CAP_USDM = 100_000;
/** Legacy default — migrate stored caps at or below this up to the new default. */
const LEGACY_AUTOPILOT_DAILY_CAP_USDM = 500;

export function loadAutopilotEnabled(): boolean {
	try {
		return localStorage.getItem(AUTOPILOT_KEY) === "1";
	} catch {
		return false;
	}
}

export function saveAutopilotEnabled(on: boolean) {
	try {
		localStorage.setItem(AUTOPILOT_KEY, on ? "1" : "0");
	} catch {
		/* ignore */
	}
}

export function loadAutopilotDailyCapUsdm(): number {
	try {
		const raw = localStorage.getItem(AUTOPILOT_DAILY_CAP_KEY);
		const v = Number(raw);
		if (Number.isFinite(v) && v > LEGACY_AUTOPILOT_DAILY_CAP_USDM) {
			return v;
		}
		// Missing, invalid, or legacy ≤500 → bump to 100k and persist.
		localStorage.setItem(
			AUTOPILOT_DAILY_CAP_KEY,
			String(DEFAULT_AUTOPILOT_DAILY_CAP_USDM),
		);
	} catch {
		/* ignore */
	}
	return DEFAULT_AUTOPILOT_DAILY_CAP_USDM;
}

function utcDayKey(): string {
	return new Date().toISOString().slice(0, 10);
}

export function getAutopilotSpentTodayUsdm(): number {
	try {
		const raw = localStorage.getItem(AUTOPILOT_SPEND_KEY);
		if (!raw) return 0;
		const parsed = JSON.parse(raw) as { day?: string; spent?: number };
		if (parsed.day !== utcDayKey()) return 0;
		return Number(parsed.spent) || 0;
	} catch {
		return 0;
	}
}

export function addAutopilotSpentTodayUsdm(amount: number) {
	if (!(amount > 0)) return;
	const day = utcDayKey();
	const prev = getAutopilotSpentTodayUsdm();
	try {
		localStorage.setItem(
			AUTOPILOT_SPEND_KEY,
			JSON.stringify({ day, spent: prev + amount }),
		);
	} catch {
		/* ignore */
	}
}

export function serializeHuntTargets(targets: HuntTargetUi[]): string {
	return targets
		.map((t) =>
			t.kind === "asset" ? `a:${t.asset}` : `c:${t.combo}`,
		)
		.sort()
		.join("|");
}

function normalizeSubaccount(
	raw: Uint8Array | number[] | ArrayBuffer | { [n: number]: number },
): Uint8Array {
	if (raw instanceof Uint8Array) {
		return raw.length === 32 ? raw : Uint8Array.from(raw);
	}
	if (raw instanceof ArrayBuffer) {
		return new Uint8Array(raw);
	}
	return Uint8Array.from(raw as ArrayLike<number>);
}

function formatGatewayError(err: unknown): string {
	if (err == null) return "unknown error";
	if (typeof err === "string") return err;
	if (typeof err !== "object") return String(err);
	const e = err as Record<string, unknown>;
	if ("Unauthorized" in e) return `Unauthorized: ${String(e.Unauthorized)}`;
	if ("Protocol" in e) return `Protocol: ${String(e.Protocol)}`;
	if ("RateLimit" in e) return `Rate limited: ${String(e.RateLimit)}`;
	if ("InsufficientCredits" in e) return "Insufficient credits";
	if ("InsufficientCycles" in e) return "Insufficient cycles";
	return formatCandidError(err);
}

function unwrapGateway<T>(r: { ok?: T; err?: unknown }, label: string): T {
	if (r && typeof r === "object" && "ok" in r && r.ok !== undefined) {
		return r.ok as T;
	}
	if (r && typeof r === "object" && "err" in r) {
		throw new Error(`${label}: ${formatGatewayError(r.err)}`);
	}
	throw new Error(`${label}: ${formatGatewayError(r)}`);
}

async function createGatewayActor(identity: Identity): Promise<_SERVICE | null> {
	if (!AGENT_GATEWAY_CANISTER_ID) return null;
	const config = getIcConfig();
	const agent = await createHttpAgent(config, identity);
	return Actor.createActor<_SERVICE>(idlFactory, {
		agent,
		canisterId: AGENT_GATEWAY_CANISTER_ID,
	});
}

export type AutopilotLiquidateResult = {
	vaultId: bigint;
	repayUsdm: number;
	prepayUsdm: number;
};

/**
 * Escrow USDM (repay + commission), then gateway.agentLiquidateVaultAmount.
 * Partial close-factor only — never full-clear.
 */
export async function executeAutopilotLiquidate(opts: {
	identity: Identity;
	protocol: ProtocolActor;
	httpAgent: HttpAgent;
	vaultId: bigint;
	/** Prefer UI max partial; preview is authoritative. */
	suggestedRepayUsdm?: number;
	commissionBps?: number;
	usdmWalletBalance: number;
}): Promise<AutopilotLiquidateResult> {
	const {
		identity,
		protocol,
		httpAgent,
		vaultId,
		suggestedRepayUsdm,
		commissionBps = 25,
		usdmWalletBalance,
	} = opts;
	const liquidator = identity.getPrincipal();
	if (liquidator.isAnonymous()) {
		throw new Error("Connect Internet Identity before autopilot liquidate");
	}

	const previewOpt = await protocol.previewLiquidation(vaultId);
	const preview = previewOpt[0];
	if (!preview) throw new Error("Vault is not liquidatable");

	const maxPartialE8 =
		preview.maxRepayE8 > 0n && preview.maxRepayE8 < preview.debtE8
			? preview.maxRepayE8
			: preview.debtE8 > 2n
				? preview.debtE8 / 2n
				: preview.debtE8;
	let repayE8 = maxPartialE8;
	if (suggestedRepayUsdm != null && suggestedRepayUsdm > 0) {
		const want = amountToNat(suggestedRepayUsdm);
		if (want > 0n && want < repayE8) repayE8 = want;
	}
	if (repayE8 <= 0n) throw new Error("Repay amount too small");
	if (repayE8 >= preview.debtE8 && preview.debtE8 > 1n) {
		repayE8 = preview.debtE8 - 1n;
	}

	const repayUsdm = natToAmount(repayE8);
	const commissionUsdm = (repayUsdm * commissionBps) / 10_000;
	const pullUsdm = repayUsdm + commissionUsdm;

	const config = getIcConfig();
	const ledgers = await protocol.getLedgerCanisters();
	const holdFeeE8 = await fetchUsdmTransferFee(httpAgent, config, ledgers);
	const holdFeeUsdm = natToAmount(holdFeeE8);
	// Escrow must cover repay+commission + escrow→treasury fee.
	const prepayUsdm = pullUsdm + holdFeeUsdm;

	const cap = loadAutopilotDailyCapUsdm();
	const spent = getAutopilotSpentTodayUsdm();
	const remaining = Math.max(0, cap - spent);
	if (spent + prepayUsdm > cap) {
		throw new Error(
			`Autopilot daily USDM cap: need ${prepayUsdm.toFixed(2)}, remaining ${remaining.toFixed(2)} of ${cap.toLocaleString()} (spent ${spent.toFixed(2)} today)`,
		);
	}
	if (usdmWalletBalance + 1e-9 < prepayUsdm) {
		throw new Error(
			`Need ~${prepayUsdm.toFixed(2)} USDM (repay+commission+fee); wallet has ${usdmWalletBalance.toFixed(2)}`,
		);
	}

	const getEscrow = protocol.getLiquidatorUsdmEscrowAccount?.bind(protocol);
	if (typeof getEscrow !== "function") {
		throw new Error("Protocol missing getLiquidatorUsdmEscrowAccount");
	}
	const esc = await getEscrow(liquidator);
	const sub = normalizeSubaccount(esc.subaccount);
	if (sub.length !== 32) {
		throw new Error(`Invalid escrow subaccount length ${sub.length}`);
	}

	await transferUsdmToLiquidatorEscrow(httpAgent, config, ledgers, prepayUsdm, {
		owner: esc.owner,
		subaccount: sub,
	});

	const usdmLedger = resolveTransferLedgerId(config, "USDM", ledgers);
	if (!usdmLedger) throw new Error("USDM ledger not configured");
	const balE8 = await fetchIcrcBalanceE8(
		httpAgent,
		usdmLedger,
		esc.owner,
		sub,
	);
	const pullE8 = amountToNat(pullUsdm);
	const needE8 = pullE8 + holdFeeE8;
	if (balE8 < needE8) {
		throw new Error(
			`USDM escrow prepay failed: ${balE8.toString()} e8 < need ~${needE8.toString()} e8`,
		);
	}

	const gateway = await createGatewayActor(identity);
	if (!gateway) throw new Error("Agent gateway not configured");
	unwrapGateway(
		(await gateway.agentLiquidateVaultAmount(vaultId, repayE8)) as {
			ok?: unknown;
			err?: unknown;
		},
		"agentLiquidateVaultAmount",
	);

	addAutopilotSpentTodayUsdm(prepayUsdm);
	return { vaultId, repayUsdm, prepayUsdm };
}
